/**
 * chat-order-report.tsx — Khối báo cáo "Hiệu quả Chat → Đơn hàng" (màn Tổng quan).
 *
 * Theo bàn giao `tong-quan-BAN-GIAO.md`, có một điều chỉnh so bản gốc:
 *  - Chỉ số 01 đổi từ "Số người đã kết bạn (trong kỳ)" → "Số liên hệ" = TỔNG
 *    liên hệ trong danh bạ hiện có, KHÔNG lọc theo kỳ. Zalo không cấp thời
 *    điểm kết bạn thật cho từng liên hệ (chỉ có lúc hệ thống ĐỒNG BỘ danh bạ),
 *    nên "kết bạn mới trong kỳ" là số bịa — một lần đồng bộ lại từng khiến
 *    báo cáo hiện "154 người kết bạn trong một ngày". Số liên hệ không có
 *    badge trend vì là ảnh chụp hiện tại (current = previous, so sánh vô nghĩa).
 *  - 10 chỉ số chia 2 nhóm (① Tương tác & Nhắn tin / ② Chuyển đổi đơn hàng),
 *    số thứ tự 01–12 theo nghiệp vụ (09, 10 chưa dùng).
 *  - Bộ lọc kỳ: Ngày / Tuần / Tháng (mặc định) / Kỳ (so sánh) ▾ / Tùy chỉnh ▾.
 *    Ở chế độ so sánh, mỗi thẻ hiện badge trend ▲/▼ % so kỳ trước.
 *  - Phễu Kết bạn → Nhắn tin → Đơn + tách đơn AI vs Nhân viên.
 *  - Biểu đồ Tin nhắn theo ngày (7 ngày, GET /dashboard/message-volume) và
 *    Tags theo hội thoại (thay Pipeline cũ).
 *
 * API chỉ trả số đếm gốc — mọi tỉ lệ và trend tính tại đây, định dạng vi-VN
 * (13.316 · 25,0% · doanh số VND hiện nguyên số: "2.496.000 ₫").
 * orders/revenue/aiOrders = null → thẻ hiện "—" + dòng chú thích amber
 * (meta.salesNote); phễu và khối AI/NV tự ẩn phần đơn hàng.
 */
import { useState } from 'react'
import dayjs from 'dayjs'
import {
  ArrowDown, Bot, CalendarRange, ChevronDown, Handshake, Info, MessageCircle, Moon,
  ShoppingCart, TrendingDown, TrendingUp, Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { useMessageVolume } from '@/hooks/use-analytics'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { groupOfPlatform, type ChannelGroupId } from '@/lib/channel-groups'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  useChatReport,
  type ChatReportCompare,
  type ChatReportMetrics,
  type ChatReportMode,
  type ChatReportScope,
} from '@/hooks/use-chat-report'
import type { ChiSoTruyVet } from '@/hooks/use-drilldown'
import { cn, formatNumber } from '@/lib/utils'

// ── Định dạng vi-VN ─────────────────────────────────────────────────

/** 25,0% — một số thập phân. Mẫu = 0 → "—" (không chia cho 0). */
function fmtPct1(numer: number, denom: number): string {
  if (!denom) return '—'
  return `${((numer / denom) * 100).toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

/**
 * VND nguyên → "2.496.000 ₫".
 *
 * Cố ý KHÔNG rút gọn thành "2 tr" hay "1,31 tỷ": đây là số tiền, người xem cần
 * đối chiếu với sổ sách và với bảng truy vết bên dưới. Làm tròn về triệu biến
 * 2.496.000 thành "2 tr" — sai gần nửa triệu ngay trên thẻ.
 */
function fmtRevenue(vnd: number): string {
  return `${formatNumber(Math.round(vnd))} ₫`
}

/** % an toàn dạng số (cho width thanh tỉ lệ). */
function pctNum(numer: number, denom: number): number {
  if (!denom) return 0
  return Math.max(0, Math.min(100, (numer / denom) * 100))
}

// ── Trend so kỳ trước ───────────────────────────────────────────────

type MetricKey =
  | 'friends' | 'msgIn' | 'msgPerFriend' | 'afterHours' | 'afterHoursPct'
  | 'chatters' | 'chattersPct' | 'afterHoursChatters' | 'afterHoursChattersPct' | 'aiConversations'
  | 'orders' | 'convRate' | 'revenue' | 'aiOrders' | 'aiPct'

/** 10 giá trị raw để so kỳ (đúng hàm raws() của mock bàn giao). */
function raws(d: ChatReportMetrics): Partial<Record<MetricKey, number>> {
  const r: Partial<Record<MetricKey, number>> = {
    friends: d.friends,
    msgIn: d.msgIn,
    afterHours: d.afterHours,
    chatters: d.chatters,
    afterHoursChatters: d.afterHoursChatters ?? 0,
  }
  if (d.friends > 0) r.msgPerFriend = d.msgIn / d.friends
  if (d.msgIn > 0) r.afterHoursPct = d.afterHours / d.msgIn
  if (d.friends > 0) r.chattersPct = d.chatters / d.friends
  if (d.chatters > 0) r.afterHoursChattersPct = (d.afterHoursChatters ?? 0) / d.chatters
  r.aiConversations = d.aiConversations ?? 0
  if (d.orders != null) {
    r.orders = d.orders
    if (d.chatters > 0) r.convRate = d.orders / d.chatters
  }
  if (d.revenue != null) r.revenue = d.revenue
  if (d.aiOrders != null) {
    r.aiOrders = d.aiOrders
    if (d.orders) r.aiPct = d.aiOrders / d.orders
  }
  return r
}

/** % thay đổi so kỳ trước — null khi thiếu dữ liệu hoặc kỳ trước = 0. */
function trendOf(
  key: MetricKey,
  cur?: Partial<Record<MetricKey, number>>,
  prev?: Partial<Record<MetricKey, number>>,
): number | null {
  const c = cur?.[key]
  const p = prev?.[key]
  if (c == null || p == null || !p) return null
  return ((c - p) / p) * 100
}

function TrendBadge({ change }: { change: number | null }) {
  if (change == null) return null
  const up = change >= 0
  const Icon = up ? TrendingUp : TrendingDown
  return (
    <span
      className={cn(
        'mt-1 inline-flex items-center gap-1 text-xs font-semibold',
        up ? 'text-success' : 'text-destructive',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {up ? '+' : '−'}
      {Math.abs(change).toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      % so kỳ trước
    </span>
  )
}

// ── Thẻ chỉ số ──────────────────────────────────────────────────────

function MetricCard({
  num, label, value, sub, giaiThich, icon: Icon, trend, barPct, accent = 'primary', onClick,
}: {
  num: string
  label: string
  value: string
  sub?: string
  /** Ghi chú cách tính — hiện luôn dưới thẻ. */
  giaiThich?: string
  icon?: typeof Handshake
  trend: number | null
  barPct?: number | null
  accent?: 'primary' | 'success' | 'warning' | 'destructive'
  /** Có hàm này thì cả thẻ bấm được để mở bảng truy vết số liệu. */
  onClick?: () => void
}) {
  const accentBorder = {
    primary: 'border-l-primary',
    success: 'border-l-success',
    warning: 'border-l-warning',
    destructive: 'border-l-destructive',
  }[accent]
  const accentIcon = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
  }[accent]
  const accentBar = {
    primary: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    destructive: 'bg-destructive',
  }[accent]

  return (
    <Card
      className={cn(
        'relative border-l-4 p-4',
        accentBorder,
        onClick && 'cursor-pointer transition-shadow hover:shadow-md',
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
      title={onClick ? 'Bấm để xem số này ở đâu ra' : undefined}
    >
      <span className="absolute right-3 top-3 text-[11px] font-bold text-muted-foreground/50">{num}</span>
      <div className="flex items-center gap-2 pr-6 text-xs font-medium text-muted-foreground">
        {Icon && (
          <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md', accentIcon)}>
            <Icon className="h-3.5 w-3.5" />
          </span>
        )}
        {label}
      </div>
      {/* Số tiền nguyên có thể dài tới 15 ký tự — hạ cỡ chữ thay vì để tràn thẻ. */}
      <p
        className={cn(
          'mt-2 font-bold tabular-nums tracking-tight',
          value.length > 12 ? 'text-lg' : value.length > 9 ? 'text-xl' : 'text-2xl',
        )}
      >
        {value}
      </p>
      <TrendBadge change={trend} />
      {sub && <p className="mt-1 text-[11px] text-muted-foreground">{sub}</p>}
      {giaiThich && (
        <p className="mt-1.5 inline-flex items-start gap-1 text-[10.5px] leading-snug text-muted-foreground/90">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{giaiThich}</span>
        </p>
      )}
      {barPct != null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div className={cn('h-full rounded-full', accentBar)} style={{ width: `${barPct}%` }} />
        </div>
      )}
    </Card>
  )
}

// ── Bộ lọc kỳ ───────────────────────────────────────────────────────

const COMPARE_OPTIONS: { value: ChatReportCompare; label: string }[] = [
  { value: '7d', label: '7 ngày / 7 ngày trước' },
  { value: 'month', label: 'Tháng này / tháng trước' },
  { value: 'quarter', label: 'Quý này / quý trước' },
  { value: 'year', label: 'Năm nay / năm trước' },
]

export function PeriodFilter({ mode, onChange }: { mode: ChatReportMode; onChange: (m: ChatReportMode) => void }) {
  const [customOpen, setCustomOpen] = useState(false)
  const [from, setFrom] = useState(dayjs().subtract(29, 'day').format('YYYY-MM-DD'))
  const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'))

  // Tuần/tháng/năm là kỳ TRƯỢT tính đến hết hôm nay (7 / 30 / 365 ngày), không
  // phải theo lịch — chú thích ghi rõ số ngày để không ai hiểu nhầm thành
  // "từ mồng 1" hay "từ 01/01".
  const segs: { key: 'day' | 'week' | 'month' | 'year' | 'all'; label: string; chu: string }[] = [
    { key: 'day', label: 'Ngày', chu: 'Hôm nay' },
    { key: 'week', label: 'Tuần', chu: '7 ngày gần nhất' },
    { key: 'month', label: 'Tháng', chu: '30 ngày gần nhất' },
    { key: 'year', label: 'Năm', chu: '365 ngày gần nhất' },
    { key: 'all', label: 'Tất cả', chu: 'Toàn bộ dữ liệu từ trước tới nay' },
  ]

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex overflow-hidden rounded-md border">
        {segs.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange({ kind: 'period', period: s.key })}
            title={s.chu}
            className={cn(
              'px-3 py-1.5 text-xs font-semibold transition-colors',
              mode.kind === 'period' && mode.period === s.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={mode.kind === 'compare' ? 'default' : 'outline'} size="sm">
            Kỳ (so sánh)
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[230px]">
          {COMPARE_OPTIONS.map((o) => (
            <DropdownMenuItem
              key={o.value}
              className={cn(
                mode.kind === 'compare' && mode.compare === o.value && 'bg-primary/10 font-semibold text-primary',
              )}
              onSelect={() => onChange({ kind: 'compare', compare: o.value })}
            >
              {o.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu open={customOpen} onOpenChange={setCustomOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant={mode.kind === 'custom' ? 'default' : 'outline'} size="sm">
            <CalendarRange className="h-3.5 w-3.5" />
            Tùy chỉnh
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[250px] p-3">
          <div className="space-y-2">
            <div>
              <Label className="text-xs text-muted-foreground">Từ ngày</Label>
              <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Đến ngày</Label>
              <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="mt-1" />
            </div>
            <Button
              className="w-full"
              size="sm"
              disabled={!from || !to || from > to}
              onClick={() => {
                onChange({ kind: 'custom', from, to })
                setCustomOpen(false)
              }}
            >
              Áp dụng
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

// ── Biểu đồ tin nhắn theo ngày (div thuần, màu theo token) ──────────

const DAY_LABELS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']

/**
 * Cột tin nhắn theo ngày, trải đúng kỳ đang lọc.
 *
 * Nhãn trục ngang đổi theo độ dài kỳ: kỳ ngắn ghi thứ (CN, T2…) vì người xem
 * nghĩ theo tuần; kỳ dài ghi ngày/tháng và chỉ ghi thưa ra, chứ 30 nhãn chồng
 * nhau thì không đọc được nhãn nào.
 */
function MessageVolumeChart({ mode, scope }: { mode: ChatReportMode; scope?: ChatReportScope }) {
  const { data, isLoading } = useMessageVolume(mode, scope)
  if (isLoading) return <Loading />
  const days = data?.data ?? []
  const max = Math.max(1, ...days.map((d) => Math.max(d.sent, d.received)))
  const total = days.reduce((s, d) => s + d.sent + d.received, 0)
  const theoTuan = days.length <= 10
  // Kỳ dài thì chỉ ghi nhãn cách quãng — tối đa khoảng 10 nhãn trên trục.
  const buocNhan = Math.max(1, Math.ceil(days.length / 10))

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="text-sm">Tin nhắn theo ngày</CardTitle>
          <CardDescription className="mt-0.5 text-xs">
            {days.length} ngày · Tổng {formatNumber(total)} tin
            {data?.truncated ? ' · đã cắt bớt phần cũ để biểu đồ còn đọc được' : ''}
          </CardDescription>
        </div>
        <div className="flex gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-primary" />
            Đã gửi
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm bg-primary/35" />
            Đã nhận
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className={cn('flex h-44 items-end justify-between', theoTuan ? 'gap-2' : 'gap-px')}>
          {days.map((d, idx) => (
            <div key={d.date} className="flex h-full min-w-0 flex-1 flex-col items-center gap-1.5">
              <div className={cn('flex w-full flex-1 items-end justify-center', theoTuan ? 'gap-1' : 'gap-px')}>
                <div
                  className={cn('rounded-t-sm bg-primary', theoTuan ? 'w-2.5' : 'w-full')}
                  style={{ height: `${Math.round((d.sent / max) * 100)}%` }}
                  title={`${dayjs(d.date).format('DD/MM')} — đã gửi: ${formatNumber(d.sent)}`}
                />
                <div
                  className={cn('rounded-t-sm bg-primary/35', theoTuan ? 'w-2.5' : 'w-full')}
                  style={{ height: `${Math.round((d.received / max) * 100)}%` }}
                  title={`${dayjs(d.date).format('DD/MM')} — đã nhận: ${formatNumber(d.received)}`}
                />
              </div>
              <span className="h-3 text-[10px] font-semibold text-muted-foreground">
                {theoTuan
                  ? DAY_LABELS[dayjs(d.date).day()]
                  : idx % buocNhan === 0
                    ? dayjs(d.date).format('DD/MM')
                    : ''}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

// ── Khối chính ──────────────────────────────────────────────────────

import { ALL_SCOPE, kenhCoDanhBa, nhanKenh, type KenhBaoCao } from './kenh-tabs'
export { ALL_SCOPE }

/**
 * Bộ lọc phạm vi (kênh tương tác + tài khoản) — dùng ở thanh lọc trên cùng
 * màn Tổng quan.
 *
 * Tách khỏi khối báo cáo vì cả thẻ KPI lẫn báo cáo bên dưới đều ăn theo phạm
 * vi này; để nó nằm trong báo cáo thì người dùng đổi tài khoản mà mấy thẻ ở
 * trên vẫn là số toàn công ty.
 */
/** Ô chọn tài khoản trong kênh đang xem — kênh do thanh tab trên cùng quyết. */
export function TaiKhoanFilter({
  channel, accountId, onChange,
}: {
  channel: string
  accountId: string
  onChange: (accountId: string) => void
}) {
  const accountsQ = useZaloAccounts()
  const accounts = (accountsQ.data ?? []).filter((a) => groupOfPlatform(a.platform) === (channel as ChannelGroupId))
  return (
    <Select value={accountId} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-52 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_SCOPE}>Tất cả tài khoản · {nhanKenh(channel as KenhBaoCao)}</SelectItem>
        {accounts.map((a) => (
          <SelectItem key={a.id} value={a.id}>{a.displayName || 'Không tên'}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * Khối báo cáo Chat → Đơn. Kỳ và phạm vi do màn Tổng quan giữ và truyền
 * xuống — một bộ lọc duy nhất cho cả trang.
 */
export function ChatOrderReport({
  mode, scope, onTruyVet, kenh,
}: {
  mode: ChatReportMode
  scope: ChatReportScope
  /** Bấm vào một thẻ số → mở bảng truy vết. Hộp thoại do màn Tổng quan giữ. */
  onTruyVet: (chiSo: ChiSoTruyVet) => void
  /** Tab kênh đang xem — quyết định bộ chỉ số (kênh không có danh bạ thì bỏ các thẻ "liên hệ"). */
  kenh: KenhBaoCao
}) {
  const { data, isLoading, isError } = useChatReport(mode, scope)
  // OA / Facebook / Web chat không đồng bộ danh bạ bạn bè → "Số liên hệ" luôn 0,
  // các tỉ lệ chia cho nó vô nghĩa. Đầu phễu ở các kênh này là người nhắn đến.
  const coDanhBa = kenhCoDanhBa(kenh)

  if (isError) return <ErrorState message="Không tải được báo cáo Chat → Đơn hàng" />

  const cur = data?.current
  const prev = data?.previous
  const curRaws = cur ? raws(cur) : undefined
  const prevRaws = prev ? raws(prev) : undefined
  const t = (k: MetricKey) => trendOf(k, curRaws, prevRaws)

  const hasOrders = cur != null && cur.orders != null
  const staffOrders = hasOrders && cur.aiOrders != null ? (cur.orders ?? 0) - cur.aiOrders : null
  const aiSharePct = hasOrders && cur.orders ? pctNum(cur.aiOrders ?? 0, cur.orders) : 0
  const tags = data?.tags ?? []
  const maxTag = Math.max(1, ...tags.map((x) => x.count))

  return (
    <div className="space-y-4">
      {/* Tiêu đề — bộ lọc kỳ và phạm vi nằm ở thanh lọc trên cùng trang */}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="border-l-4 border-primary pl-3 text-base font-bold">
          Báo cáo hiệu quả Chat → Đơn hàng · {nhanKenh(kenh)}
        </h2>
        <span className="text-xs text-muted-foreground">Kỳ: {data?.label ?? '…'}</span>
      </div>
      {!coDanhBa && (
        <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
          Kênh này không có danh bạ bạn bè (chỉ Zalo cá nhân mới đồng bộ bạn bè) nên không có chỉ số "Số liên hệ" và tỉ trọng theo liên hệ; đầu phễu là số người nhắn đến.
        </p>
      )}

      {data?.meta.scopeNote && (
        <p className="rounded-md bg-warning/10 px-3 py-2 text-xs text-warning">
          {data.meta.scopeNote}
        </p>
      )}

      {isLoading || !cur ? (
        <Loading />
      ) : (
        <>
          {/* ① Tương tác & Nhắn tin */}
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            ① Tương tác &amp; Nhắn tin
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {coDanhBa && <MetricCard num="01" onClick={() => onTruyVet('friends')} label="Số liên hệ" icon={Users} accent="primary"
              value={formatNumber(cur.friends)} sub="tổng liên hệ trong danh bạ" trend={null}
              giaiThich="Bạn bè Zalo đã đồng bộ về từ các nick được xem, đếm theo từng nick (một người kết bạn 2 nick tính 2) và gồm cả người chưa từng nhắn tin. Vì vậy khác “Tổng khách hàng” (chỉ người đã có hội thoại, tính 1 lần)." />}
            <MetricCard num="02" onClick={() => onTruyVet('chatters')} label={coDanhBa ? 'Số liên hệ có nhắn tin đến' : 'Số người nhắn đến'} icon={MessageCircle} accent="primary"
              value={formatNumber(cur.chatters)} sub={`${coDanhBa ? 'liên hệ' : 'người'} có gửi tin trong kỳ · ${formatNumber(cur.msgIn)} tin`} trend={t('chatters')}
              giaiThich={`Mỗi ${coDanhBa ? 'liên hệ' : 'người'} tính 1 lần dù nhắn nhiều tin hay nhắn tới nhiều tài khoản.`} />
            {coDanhBa && <MetricCard num="06" onClick={() => onTruyVet('chattersPct')} label="Tỉ trọng liên hệ nhắn đến / liên hệ" icon={TrendingUp} accent="primary"
              value={fmtPct1(cur.chatters, cur.friends)}
              sub="liên hệ có nhắn tin trên tổng liên hệ trong danh bạ" trend={t('chattersPct')}
              barPct={pctNum(cur.chatters, cur.friends)} />}
            <MetricCard num="07" onClick={() => onTruyVet('afterHoursChatters')} label={coDanhBa ? 'Liên hệ nhắn đến ngoài giờ' : 'Người nhắn đến ngoài giờ'} icon={Moon} accent="warning"
              value={formatNumber(cur.afterHoursChatters ?? 0)}
              sub={`có tin đến ngoài khung ${data?.meta.afterHoursWindow ?? '08:00–18:00'} · ${formatNumber(cur.afterHours)} tin`} trend={t('afterHoursChatters')} />
            <MetricCard num="08" onClick={() => onTruyVet('afterHoursChattersPct')} label="Tỉ trọng ngoài giờ / tổng nhắn đến" icon={Moon} accent="warning"
              value={fmtPct1(cur.afterHoursChatters ?? 0, cur.chatters)} sub={coDanhBa ? 'liên hệ nhắn ngoài giờ trên liên hệ có nhắn tin đến' : 'người nhắn ngoài giờ trên tổng người nhắn đến'}
              trend={t('afterHoursChattersPct')} barPct={pctNum(cur.afterHoursChatters ?? 0, cur.chatters)} />
          </div>

          {/* ② Chuyển đổi đơn hàng */}
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            ② Chuyển đổi đơn hàng
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            <MetricCard num="03" onClick={() => onTruyVet('orders')} label="Đơn hàng từ hội thoại" icon={ShoppingCart} accent="success"
              value={cur.orders != null ? formatNumber(cur.orders) : '—'}
              sub="đơn chốt qua chat" trend={t('orders')} />
            <MetricCard num="04" onClick={() => onTruyVet('convRate')} label="Tỉ lệ đơn / hội thoại" icon={ShoppingCart} accent="success"
              value={cur.orders != null ? fmtPct1(cur.orders, cur.chatters) : '—'}
              sub={cur.orders != null ? `${formatNumber(cur.orders)} đơn / ${formatNumber(cur.chatters)} hội thoại có khách nhắn` : undefined}
              trend={t('convRate')}
              barPct={cur.orders != null ? Math.min(100, pctNum(cur.orders, cur.chatters) * 5) : null} />
            <MetricCard num="05" onClick={() => onTruyVet('revenue')} label="Doanh số từ hội thoại" icon={TrendingUp} accent="success"
              value={cur.revenue != null ? fmtRevenue(cur.revenue) : '—'}
              sub={
                cur.revenue != null && cur.orders
                  ? `TB ${formatNumber(Math.round(cur.revenue / cur.orders))} ₫ / đơn`
                  : undefined
              }
              trend={t('revenue')} />
            <MetricCard num="09" onClick={() => onTruyVet('aiConversations')} label="Hội thoại có AI trả lời" icon={Bot} accent="primary"
              value={formatNumber(cur.aiConversations ?? 0)}
              sub="AI tự gửi ít nhất 1 tin trong kỳ" trend={t('aiConversations')} />
            <MetricCard num="11" onClick={() => onTruyVet('aiOrders')} label="Đơn đến từ AI" icon={Bot} accent="primary"
              value={cur.aiOrders != null ? formatNumber(cur.aiOrders) : '—'}
              sub="do AI chat tư vấn & chốt đơn" trend={t('aiOrders')} />
            <MetricCard num="12" onClick={() => onTruyVet('aiPct')} label="Tỉ trọng đơn AI chốt" icon={Bot} accent="primary"
              value={cur.aiOrders != null && cur.orders ? fmtPct1(cur.aiOrders, cur.orders) : '—'}
              sub={
                cur.aiOrders != null && cur.orders != null
                  ? `${formatNumber(cur.aiOrders)} / ${formatNumber(cur.orders)} đơn từ chat`
                  : undefined
              }
              trend={t('aiPct')} barPct={cur.aiOrders != null && cur.orders ? aiSharePct : null} />
          </div>

          {data?.meta.salesNote && (
            <p className="rounded-md bg-warning/10 px-3 py-2 text-xs text-warning">
              {data.meta.salesNote}
            </p>
          )}

          {/* Phễu + AI vs Nhân viên */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Phễu chuyển đổi</CardTitle>
                <CardDescription className="text-xs">{coDanhBa ? 'Liên hệ → Nhắn tin → Đơn hàng' : 'Người nhắn đến → Đơn hàng'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {coDanhBa && (
                  <>
                    <div className="flex items-center gap-3 rounded-lg bg-primary p-3 text-primary-foreground">
                      <Users className="h-5 w-5" />
                      <div>
                        <p className="text-xs font-medium opacity-90">Liên hệ</p>
                        <p className="text-lg font-bold tabular-nums">{formatNumber(cur.friends)}</p>
                      </div>
                    </div>
                    <p className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                      <ArrowDown className="h-3 w-3" />
                      {fmtPct1(cur.chatters, cur.friends)} có nhắn tin
                    </p>
                  </>
                )}
                <div className="flex items-center gap-3 rounded-lg bg-primary/80 p-3 text-primary-foreground">
                  <Users className="h-5 w-5" />
                  <div>
                    <p className="text-xs font-medium opacity-90">Người có nhắn tin</p>
                    <p className="text-lg font-bold tabular-nums">{formatNumber(cur.chatters)}</p>
                  </div>
                </div>
                {hasOrders ? (
                  <>
                    <p className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground">
                      <ArrowDown className="h-3 w-3" />
                      {fmtPct1(cur.orders ?? 0, cur.chatters)} ra đơn
                    </p>
                    <div className="flex items-center gap-3 rounded-lg bg-success p-3 text-success-foreground">
                      <ShoppingCart className="h-5 w-5" />
                      <div className="flex-1">
                        <p className="text-xs font-medium opacity-90">Đơn hàng chốt</p>
                        <p className="text-lg font-bold tabular-nums">{formatNumber(cur.orders ?? 0)}</p>
                      </div>
                      {cur.revenue != null && (
                        <span className="rounded-full bg-success-foreground/20 px-3 py-1 text-xs font-bold">
                          {fmtRevenue(cur.revenue)}
                        </span>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="rounded-lg bg-muted p-3 text-center text-xs text-muted-foreground">
                    Chưa có dữ liệu đơn hàng trong kỳ này
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Đơn hàng: AI vs Nhân viên</CardTitle>
                <CardDescription className="text-xs">Nguồn chốt đơn qua chat</CardDescription>
              </CardHeader>
              <CardContent>
                {hasOrders && staffOrders != null && (cur.orders ?? 0) > 0 ? (
                  <>
                    <div className="mb-3 flex h-4 overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-primary" style={{ width: `${aiSharePct}%` }} />
                      <div className="h-full bg-success" style={{ width: `${100 - aiSharePct}%` }} />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-sm bg-primary" />
                        AI chốt tự động
                        <b className="ml-auto tabular-nums">{formatNumber(cur.aiOrders ?? 0)}</b>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-sm bg-success" />
                        Nhân viên chốt
                        <b className="ml-auto tabular-nums">{formatNumber(staffOrders)}</b>
                      </div>
                    </div>
                    <p className="mt-3 rounded-md bg-muted p-2.5 text-xs leading-relaxed text-muted-foreground">
                      <Bot className="mr-1 inline h-3.5 w-3.5" />
                      AI đang đảm nhận <b>{fmtPct1(cur.aiOrders ?? 0, cur.orders ?? 0)}</b> lượng đơn chốt
                      qua chat trong kỳ.
                    </p>
                  </>
                ) : (
                  <p className="rounded-lg bg-muted p-3 text-center text-xs text-muted-foreground">
                    Chưa có dữ liệu đơn hàng để tách nguồn AI / Nhân viên
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Biểu đồ */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_2fr]">
            <MessageVolumeChart mode={mode} scope={scope} />
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Tags theo hội thoại</CardTitle>
                <CardDescription className="text-xs">
                  Nhãn gắn trên hội thoại có tương tác trong kỳ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2.5">
                {tags.length === 0 ? (
                  <p className="py-4 text-center text-xs text-muted-foreground">
                    Chưa có hội thoại gắn tag trong kỳ này
                  </p>
                ) : (
                  tags.map((tag) => (
                    <div key={tag.name} className="flex items-center gap-2.5">
                      {/* Chip dùng đúng màu của tag (dữ liệu từ danh sách tag của org) */}
                      <span
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
                        style={{ color: tag.color, backgroundColor: tag.bg }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tag.color }} />
                        {tag.name}
                      </span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${Math.max(4, (tag.count / maxTag) * 100)}%` }}
                        />
                      </div>
                      <span className="w-10 shrink-0 text-right text-xs font-bold tabular-nums">
                        {formatNumber(tag.count)}
                      </span>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
